import React, {useEffect, useState} from "react";
import {useParams, useLocation, useHistory} from "react-router-dom";
import {useLocalStorage, VisuallyHidden} from "@unibuddy/patron";
import {useTranslation} from "@unibuddy/intl";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";
import {useQuery} from "react-apollo";
import isEqual from "lodash/isEqual";

import PageHeader from "shared/Shared/components/PageHeader/PageHeader";
import {
    PRESIGNUP_MESSAGE_KEY,
    QUESTION_PROMPTS_ENABLED,
    SEND_BEFORE_SIGNUP_ENABLED,
} from "shared/constants";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {pageNames, sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";
import translateIntroMessage from "shared/Chat/utils/translateIntroMessage";
import publicMentorIntroMessageQuery from "shared/Chat/queries/publicMentorIntroMessageQuery";

import {PageTitle} from "../../../../../components/PageTitle/PageTitle";
import s from "../Chat/Chat.pcss";
import LoadingChat from "../../components/LoadingChat/LoadingChat";
import {getMentorInboxInfoOne} from "../../utils/InboxInfo";
import {StyledChatComponent} from "../common/StyledChatComponent";
import {Chat as ChatV2} from "../../components/Chat/Chat";
import {CreateAccountPopup} from "../../components/CreateAccountPopupV2/CreateAccountPopup";
import {IntlNamespaces} from "../../../../../translations/types";
import {INTL_CONVERSATION_WITH} from "../../../../../translations/chat";
import {isFunction} from "../../../../../util/isFunction";
import {INTL_INBOX} from "../../../../../translations/inbox";
import {useUniversity} from "../../../../modules/UniversityProvider/UniversityProvider";
import {QuestionsPromptPopUp} from "../../components/QuestionsPromptPopUp/QuestionsPromptPopUp";
import {useIsScreenSizeLargerThan} from "../../../../../hooks/useIsScreenSizeLargerThan";

export const ANON_USER_ID = "anon-user";

export function replaceDummyChatURLWithActualURL(pathname) {
    return pathname.replace(new RegExp("\\bpre\\b"), "start");
}

const DummyChatComponent = () => {
    const isSBSEnabled = useBooleanFeatureFlag(
        SEND_BEFORE_SIGNUP_ENABLED.key,
        SEND_BEFORE_SIGNUP_ENABLED.value,
    );
    const isQuestionPromptsEnabled = useBooleanFeatureFlag(
        QUESTION_PROMPTS_ENABLED.key,
        QUESTION_PROMPTS_ENABLED.value,
    );
    const isQuestionPromptsAndSBSEnabled = isSBSEnabled && isQuestionPromptsEnabled;
    const params = useParams();
    const {trackEvent} = useAnalytics();
    const university = useUniversity();
    const {pathname} = useLocation();
    const [isQuestionsPopupOpen, setisQuestionsPopupOpen] = useState(true);
    const handleQuestionsClose = () => setisQuestionsPopupOpen(false);
    const history = useHistory();
    const popupScreenSize = useIsScreenSizeLargerThan(632);
    const {t} = useTranslation([
        IntlNamespaces.INBOX,
        IntlNamespaces.CHAT,
        IntlNamespaces.LEVELS,
        IntlNamespaces.MENTOR_CARD,
    ]);
    const isDegreeTypesEnabled = university?.features.get("enableDegreeTypes");

    const [messages, setMessages] = useState([]);

    const [preSignUpMessage, setPreSignUpMessage] = useLocalStorage(
        PRESIGNUP_MESSAGE_KEY(params?.opponentId),
        null,
    );

    const {data, loading, error} = useQuery(publicMentorIntroMessageQuery, {
        variables: {
            mentorId: params?.opponentId,
            isDegreeTypesEnabled,
        },
        skip: !params?.opponentId,
    });

    useEffect(() => {
        if (!data?.publicMentorIntroMessage?.introMessage) return;

        const introMessage = data?.publicMentorIntroMessage?.introMessage;
        const translatedIntroMessage = translateIntroMessage(introMessage, t);
        const updatedMessages = [translatedIntroMessage] ?? [];

        if (preSignUpMessage !== null) {
            updatedMessages.push(preSignUpMessage);
        }

        if (!isEqual(updatedMessages, messages)) {
            setMessages(updatedMessages);
        }
    }, [data, messages, preSignUpMessage, t]);

    useEffect(() => {
        if (loading) return;

        trackEvent(sharedEvents.pageViewed.name, {
            universitySlug: university?.slug,
            prefix: pageNames.dummyChat,
            page: pageNames.dummyChat,
        });
    }, [loading, trackEvent, university.slug]);

    if (loading) return <LoadingChat />;
    if (error) return <> An error occurred. Try again later.</>;

    const introMessage = data?.publicMentorIntroMessage?.introMessage;
    const name = introMessage?.sender?.firstName ?? "Buddy";
    const sender = introMessage?.sender ?? {};

    const onCreateAccount = () => {
        history.replace({
            pathname: replaceDummyChatURLWithActualURL(pathname),
            search: location.search,
        });
    };

    const onSend = (message) => {
        const parsedMessage = {
            id: `pre-sign-up-msg-${ANON_USER_ID}-${params?.opponentId}`,
            created: new Date().toISOString(),
            text: message.text,
            read: false,
            sender: {
                id: ANON_USER_ID,
            },
        };

        setPreSignUpMessage(parsedMessage);
        setMessages([...messages, parsedMessage]);

        trackEvent(sharedEvents.fauxChatSendMessageButtonClicked.name, {
            universitySlug: university?.slug,
            cta: "Send Message",
            page: pageNames.dummyChat,
        });
    };

    const chatGroup = {
        opponent: {
            ...sender,
            inboxInfoOne:
                getMentorInboxInfoOne({
                    sender,
                    university,
                    t,
                }) ||
                sender?.degree?.name ||
                sender?.staffFields?.department,
            inboxInfoTwo: sender?.university?.name,
            isEnterprise: sender?.university?.isEnterprise,
        },
    };

    const showQuestions =
        isSBSEnabled &&
        isQuestionPromptsAndSBSEnabled &&
        preSignUpMessage === null &&
        isQuestionsPopupOpen &&
        introMessage?.sender?.accountRole === "mentor" && // Ensure that the chat is a student chat
        popupScreenSize &&
        !pathname.includes("external");

    const showSBS = isSBSEnabled && preSignUpMessage === null && !pathname.includes("external");

    const showAccountPopup =
        !isQuestionPromptsAndSBSEnabled ||
        (isQuestionPromptsAndSBSEnabled && preSignUpMessage) ||
        (isSBSEnabled && preSignUpMessage) ||
        pathname.includes("external") ||
        !popupScreenSize;

    const chatInputHidden = (showAccountPopup && !showSBS) || showQuestions;

    const getPopup = () => {
        if (showQuestions) {
            trackEvent(sharedEvents.questionsPopUpVisible.name, {
                universitySlug: university?.slug,
            });

            return (
                <QuestionsPromptPopUp
                    onClose={handleQuestionsClose}
                    // Getting the questions starting from index 3 to avoid emoji at the start
                    onQuestionSelect={(question) =>
                        onSend({text: question.substring(3), chatGroupId: chatGroup.id})
                    }
                />
            );
        } else if (showAccountPopup && !showSBS) {
            return (
                <CreateAccountPopup
                    ambassadorFirstName={name}
                    onClick={onCreateAccount}
                    customPreSignupText={university?.customPreSignupText ?? ""}
                />
            );
        }
        return null;
    };

    return (
        <div id="dummy-chat">
            <PageTitle
                title={t(
                    `${IntlNamespaces.CHAT}:${INTL_CONVERSATION_WITH.key}`,
                    isFunction(INTL_CONVERSATION_WITH.defaultValue)
                        ? INTL_CONVERSATION_WITH.defaultValue(name)
                        : INTL_CONVERSATION_WITH.defaultValue,

                    {name},
                )}
            />
            <VisuallyHidden>
                <PageHeader
                    title={t(`${IntlNamespaces.INBOX}:${INTL_INBOX.key}`, INTL_INBOX.defaultValue)}
                />
            </VisuallyHidden>
            <div className={s.container}>
                <StyledChatComponent className={s.chat}>
                    <ChatV2
                        user={{id: ANON_USER_ID}}
                        messages={messages}
                        onSend={isSBSEnabled ? onSend : undefined}
                        chatGroup={chatGroup}
                        chatFooterHidden={chatInputHidden}
                        chatInputDisabled={chatInputHidden}
                        chatPopup={getPopup()}
                    />
                </StyledChatComponent>
            </div>
        </div>
    );
};

export default DummyChatComponent;
